'use client'

import { useState } from 'react'
import Link from 'next/link'
import { Button } from 'flowbite-react'
import { isAfter } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { isNil } from 'lodash'
import { Event } from '@/types'
import { JoinRejectedNotice } from '@/components/common'
import { formatDate } from '@/utils/formatDate'
import { useUserInfo } from '@/app/web/context/UserInfoContext'
import { checkEventEligibility } from '../../actions'

interface ActionButtonProps {
  href?: string
  onClick?: () => void
  label: string
  disabled?: boolean
}

// Flowbite's Button types `disabled` only for the plain-button variant, not
// the Link-as-button one, so branching keeps `as` a literal in each JSX call
// rather than a union that neither variant actually accepts.
const ActionButton = ({
  href,
  onClick,
  label,
  disabled,
}: ActionButtonProps) => {
  if (href) {
    const isExternal = href.startsWith('http')

    return (
      <Button
        as={Link}
        href={href}
        target={isExternal ? '_blank' : undefined}
        className="w-full lg:min-w-64"
        color="dark"
        onClick={onClick}
      >
        {label}
      </Button>
    )
  }

  return (
    <Button
      className="w-full lg:min-w-64"
      color="dark"
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </Button>
  )
}

const DiscordLink = ({ event }: { event: Event }) => {
  if (event?.communication_link?.icon !== 'discord') return null

  return (
    <Link
      href={event?.communication_link?.link || ''}
      className="flex justify-center items-center gap-x-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 py-2 shadow-sm transition-colors cursor-pointer select-none"
      target="_blank"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 640 640"
        fill="white"
        width="25"
        height="25"
      >
        <path d="M524.5 133.8C524.3 133.5 524.1 133.2 523.7 133.1C485.6 115.6 445.3 103.1 404 96C403.6 95.9 403.2 96 402.9 96.1C402.6 96.2 402.3 96.5 402.1 96.9C396.6 106.8 391.6 117.1 387.2 127.5C342.6 120.7 297.3 120.7 252.8 127.5C248.3 117 243.3 106.8 237.7 96.9C237.5 96.6 237.2 96.3 236.9 96.1C236.6 95.9 236.2 95.9 235.8 95.9C194.5 103 154.2 115.5 116.1 133C115.8 133.1 115.5 133.4 115.3 133.7C39.1 247.5 18.2 358.6 28.4 468.2C28.4 468.5 28.5 468.7 28.6 469C28.7 469.3 28.9 469.4 29.1 469.6C73.5 502.5 123.1 527.6 175.9 543.8C176.3 543.9 176.7 543.9 177 543.8C177.3 543.7 177.7 543.4 177.9 543.1C189.2 527.7 199.3 511.3 207.9 494.3C208 494.1 208.1 493.8 208.1 493.5C208.1 493.2 208.1 493 208 492.7C207.9 492.4 207.8 492.2 207.6 492.1C207.4 492 207.2 491.8 206.9 491.7C191.1 485.6 175.7 478.3 161 469.8C160.7 469.6 160.5 469.4 160.3 469.2C160.1 469 160 468.6 160 468.3C160 468 160 467.7 160.2 467.4C160.4 467.1 160.5 466.9 160.8 466.7C163.9 464.4 167 462 169.9 459.6C170.2 459.4 170.5 459.2 170.8 459.2C171.1 459.2 171.5 459.2 171.8 459.3C268 503.2 372.2 503.2 467.3 459.3C467.6 459.2 468 459.1 468.3 459.1C468.6 459.1 469 459.3 469.2 459.5C472.1 461.9 475.2 464.4 478.3 466.7C478.5 466.9 478.7 467.1 478.9 467.4C479.1 467.7 479.1 468 479.1 468.3C479.1 468.6 479 468.9 478.8 469.2C478.6 469.5 478.4 469.7 478.2 469.8C463.5 478.4 448.2 485.7 432.3 491.6C432.1 491.7 431.8 491.8 431.6 492C431.4 492.2 431.3 492.4 431.2 492.7C431.1 493 431.1 493.2 431.1 493.5C431.1 493.8 431.2 494 431.3 494.3C440.1 511.3 450.1 527.6 461.3 543.1C461.5 543.4 461.9 543.7 462.2 543.8C462.5 543.9 463 543.9 463.3 543.8C516.2 527.6 565.9 502.5 610.4 469.6C610.6 469.4 610.8 469.2 610.9 469C611 468.8 611.1 468.5 611.1 468.2C623.4 341.4 590.6 231.3 524.2 133.7zM222.5 401.5C193.5 401.5 169.7 374.9 169.7 342.3C169.7 309.7 193.1 283.1 222.5 283.1C252.2 283.1 275.8 309.9 275.3 342.3C275.3 375 251.9 401.5 222.5 401.5zM417.9 401.5C388.9 401.5 365.1 374.9 365.1 342.3C365.1 309.7 388.5 283.1 417.9 283.1C447.6 283.1 471.2 309.9 470.7 342.3C470.7 375 447.5 401.5 417.9 401.5z" />
      </svg>
      <span className="font-bold text-sm">Join Discord</span>
    </Link>
  )
}

export const BrowseMoreCTA = () => (
  <ActionButton href="/web/events" label="Browse More Hackathons" />
)

export const JoinedCTA = () => (
  <Button className="w-full lg:min-w-64" color="dark" disabled>
    Joined
  </Button>
)

// Everything a "Join Hackathon" button needs to decide what it does when
// clicked. Shared by the header CTA and the bottom-of-page CTA so the two
// buttons can never drift apart.
const useJoinHackathon = (
  event: Event,
  currentEventUrl: string,
  userId: string | undefined
) => {
  const currentDate = new Date(
    toZonedTime(new Date(), event.zoneinfo)
  ).getTime()

  const formattedRegistrationEndDate = new Date(
    formatDate(
      event.registration_end_date,
      'dd MMM yyyy, HH:mm',
      event.zoneinfo
    )
  ).getTime()

  const formattedSubmissionStartDate = event.submission_start_date
    ? new Date(
        formatDate(
          event.submission_start_date,
          'dd MMM yyyy, HH:mm',
          event.zoneinfo
        )
      ).getTime()
    : null
  const formattedSubmissionEndDate = event.submission_end_date
    ? new Date(
        formatDate(
          event.submission_end_date,
          'dd MMM yyyy, HH:mm',
          event.zoneinfo
        )
      ).getTime()
    : null

  const { userInfo } = useUserInfo()
  const isProfileIncomplete = !!(!userInfo && userId)
  const showProfileModal = () =>
    document.getElementById('update_profile_btn')?.click()

  // Populated when a signed-in member's own eligibility check refuses the
  // join — see handleJoinHackathon below. A guest has no profile to check yet,
  // so this never fires for them; their refusal, if any, still surfaces later
  // in the registration form itself.
  const [joinRejections, setJoinRejections] = useState<string[]>([])
  const [isCheckingEligibility, setIsCheckingEligibility] = useState(false)

  const handleJoinHackathon = async (registration_url?: string | null) => {
    if (!userId) {
      if (event.allow_custom_stackup_registration) {
        window.location.href = `${currentEventUrl}/registration`
      } else {
        const currentUrl = window.location.href
        localStorage.setItem('signup_return_url', currentUrl)

        const finalUrl = encodeURIComponent(currentUrl)
        const callbackUrl = `${window.location.origin}/auth/rails-callback?final_url=${finalUrl}`
        const registerUrl = `${process.env.NEXT_PUBLIC_STACKUP_HOST}/web/create-account?redirect_url=${encodeURIComponent(callbackUrl)}`
        window.location.href = registerUrl
      }
      return
    }

    if (!registration_url) return

    setJoinRejections([])

    // A member's eligibility is decided entirely by their profile — the
    // AngelHack Account form that would otherwise collect it is hidden from
    // anyone already signed in — so the outcome is already known before they
    // navigate anywhere. Checking here means a refusal shows on this page
    // instead of after a page load into a form that was always going to be
    // rejected.
    if (event.customized) {
      setIsCheckingEligibility(true)
      const result = await checkEventEligibility(event.slug, userId)
      setIsCheckingEligibility(false)

      if (!result.eligible) {
        setJoinRejections(result.errors)
        return
      }
    }

    window.location.href = registration_url
  }

  return {
    currentDate,
    formattedRegistrationEndDate,
    formattedSubmissionStartDate,
    formattedSubmissionEndDate,
    isProfileIncomplete,
    showProfileModal,
    joinRejections,
    isCheckingEligibility,
    handleJoinHackathon,
  }
}

interface JoinHackathonCTAProps {
  event: Event
  currentEventUrl: string
  userId: string | undefined
  selectTab: (value: number) => void
}

// The header call to action: whichever button suits the event's current state,
// join being only one of them.
export const JoinHackathonCTA = ({
  event,
  currentEventUrl,
  userId,
  selectTab,
}: JoinHackathonCTAProps) => {
  const {
    currentDate,
    formattedRegistrationEndDate,
    formattedSubmissionStartDate,
    formattedSubmissionEndDate,
    isProfileIncomplete,
    showProfileModal,
    joinRejections,
    isCheckingEligibility,
    handleJoinHackathon,
  } = useJoinHackathon(event, currentEventUrl, userId)

  const joinButton = (
    <ActionButton
      onClick={() =>
        handleJoinHackathon(
          event?.registration_url || `${currentEventUrl}/registration`
        )
      }
      label={isCheckingEligibility ? 'Checking…' : 'Join Hackathon'}
      disabled={isCheckingEligibility}
    />
  )

  const renderCTA = () => {
    if (event.state === 'upcoming' || event.state === 'closed') {
      return <BrowseMoreCTA />
    }

    if (event.state === 'register_now') {
      if (event?.registration_url) {
        return (
          <ActionButton href={event.registration_url} label="Join Hackathon" />
        )
      }
      if (isProfileIncomplete) {
        return (
          <ActionButton onClick={showProfileModal} label="Join Hackathon" />
        )
      }
      if (event.participation_info?.is_registered) {
        return (
          <>
            <DiscordLink event={event} />
            <BrowseMoreCTA />
            <JoinedCTA />
          </>
        )
      }
      return joinButton
    }

    if (event.state === 'ongoing') {
      const registrationClosed = isAfter(
        currentDate,
        formattedRegistrationEndDate
      )

      if (
        (isNil(formattedSubmissionStartDate) ||
          isNil(formattedSubmissionEndDate)) &&
        registrationClosed
      ) {
        return <BrowseMoreCTA />
      }

      if (event.participation_info?.is_registered) {
        const submissionEnded =
          (formattedSubmissionEndDate &&
            isAfter(currentDate, formattedSubmissionEndDate)) ||
          event.participation_info?.submission_made

        if (submissionEnded) {
          return (
            <>
              <DiscordLink event={event} />
              <BrowseMoreCTA />
              <JoinedCTA />
            </>
          )
        }
        return <ActionButton onClick={() => selectTab(2)} label="Submit Now" />
      }

      return registrationClosed ? <BrowseMoreCTA /> : joinButton
    }

    return null
  }

  return (
    <>
      <JoinRejectedNotice reasons={joinRejections} />
      {renderCTA()}
    </>
  )
}

// The bottom-of-page call to action: only ever the join button, and only for
// someone who can still act on it. Anyone who cannot — the event has not
// opened, registration has closed, the event is over, or they already joined —
// has nothing to do here, and a second "Joined"/"Browse more" pair at the foot
// of the page would only repeat what the header already says.
export const PageBottomJoinCTA = ({
  event,
  currentEventUrl,
  userId,
}: Omit<JoinHackathonCTAProps, 'selectTab'>) => {
  const {
    currentDate,
    formattedRegistrationEndDate,
    isProfileIncomplete,
    showProfileModal,
    joinRejections,
    isCheckingEligibility,
    handleJoinHackathon,
  } = useJoinHackathon(event, currentEventUrl, userId)

  const isJoinable = () => {
    if (event.state !== 'register_now' && event.state !== 'ongoing') {
      return false
    }
    if (event.participation_info?.is_registered) return false
    // An ongoing event can still be inside its registration window (late
    // joins) or past it, and only the former can be joined.
    if (
      event.state === 'ongoing' &&
      isAfter(currentDate, formattedRegistrationEndDate)
    ) {
      return false
    }
    return true
  }

  if (!isJoinable()) return null

  // Mirrors the header's join button for the same state, so both do the same
  // thing when clicked.
  const joinButton = () => {
    if (event.state === 'register_now') {
      if (event?.registration_url) {
        return (
          <ActionButton href={event.registration_url} label="Join Hackathon" />
        )
      }
      if (isProfileIncomplete) {
        return (
          <ActionButton onClick={showProfileModal} label="Join Hackathon" />
        )
      }
    }

    return (
      <ActionButton
        onClick={() =>
          handleJoinHackathon(
            event?.registration_url || `${currentEventUrl}/registration`
          )
        }
        label={isCheckingEligibility ? 'Checking…' : 'Join Hackathon'}
        disabled={isCheckingEligibility}
      />
    )
  }

  return (
    // The negative margin eats part of the trailing padding of the last
    // content section (every one of them ends in py-14), which otherwise left
    // the button stranded a long way below the text it follows.
    <div className="flex -mt-8 lg:-mt-10">
      {/* Lines the button up with the content column, which sits beside the
          section nav on large screens. */}
      <div className="hidden lg:block lg:w-1/5" />
      <div className="w-full lg:w-4/5 flex flex-col items-start gap-y-4">
        <JoinRejectedNotice reasons={joinRejections} />
        {/* Full width on a phone, the way the header button is; capped once
            there is a column wide enough for it to look odd stretched. */}
        <div className="w-full lg:max-w-xs">{joinButton()}</div>
      </div>
    </div>
  )
}
